'use client'

import React, { useEffect, useRef, useState } from 'react'
import { Bug, Loader2 } from 'lucide-react'
import type { ProcessedTheme } from './ProcessedEntry/DomainEntry/processedTypes'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

// Used only if /api/bugreport/config hasn't resolved yet (or
// fails) by the time the user clicks — keeps the widget working even if
// that fetch is briefly down, since it's just a mailto recipient.
const FALLBACK_BUGS_EMAIL = 'bugs@mail.trustfence.ai'

// Separated from the rest of the pipeline so a rejection here (the user
// dismissing the share picker) can be told apart from a genuine capture
// failure below — both can throw a same-named NotAllowedError for
// unrelated reasons.
async function requestScreenStream(): Promise<MediaStream | null> {
  try {
    // Native Screen Capture API — no third-party lib. Captures actual
    // rendered pixels (canvas/SVG/video included), unlike a DOM-redraw
    // approach, which matters for a bug-report screenshot. preferCurrentTab
    // (Chrome-only) skips the "pick a window" step and shares this tab
    // directly; other browsers just ignore the unknown constraint and fall
    // back to their normal picker.
    return await navigator.mediaDevices.getDisplayMedia({
      video: { displaySurface: 'browser' } as MediaTrackConstraints,
      preferCurrentTab: true,
      selfBrowserSurface: 'include',
    } as DisplayMediaStreamOptions)
  } catch (err: any) {
    if (err?.name === 'NotAllowedError' || err?.name === 'AbortError') return null
    throw err
  }
}

// Captures one frame as a PNG blob — deliberately doesn't touch the
// clipboard. getDisplayMedia() itself already consumes the click's user
// activation in Firefox, so a clipboard.write() chained right after it
// reliably fails there ("Clipboard write was blocked due to lack of user
// activation"), even though the exact same call succeeds fine in Chrome.
// The fix isn't more waiting — activation doesn't come back — it's a
// second, genuinely fresh click dedicated to the copy step (see the
// preview dialog below).
async function captureScreenshotBlob(stream: MediaStream): Promise<Blob> {
  try {
    const video = document.createElement('video')
    video.srcObject = stream
    video.muted = true
    video.playsInline = true
    await video.play()

    // videoWidth/videoHeight aren't guaranteed populated the instant
    // play() resolves — wait for the real metadata/resize event instead
    // of a fixed number of animation frames, with a timeout as a safety
    // net so this can't hang forever.
    if (video.videoWidth === 0 || video.videoHeight === 0) {
      await new Promise<void>((resolve) => {
        const done = () => {
          video.removeEventListener('loadedmetadata', done)
          video.removeEventListener('resize', done)
          resolve()
        }
        video.addEventListener('loadedmetadata', done, { once: true })
        video.addEventListener('resize', done, { once: true })
        setTimeout(done, 1500)
      })
    }
    if (video.videoWidth === 0 || video.videoHeight === 0) {
      throw new Error('Captured stream reported no video dimensions')
    }

    const canvas = document.createElement('canvas')
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('Canvas 2D context unavailable')
    ctx.drawImage(video, 0, 0)

    const blob: Blob | null = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'))
    if (!blob) throw new Error('Could not encode the screenshot as PNG')
    return blob
  } finally {
    stream.getTracks().forEach((t) => t.stop())
  }
}

async function writeBlobToClipboard(blob: Blob): Promise<void> {
  if (!navigator.clipboard?.write) throw new Error('This browser does not support writing images to the clipboard')
  await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })])
}

function buildMailto({ email, view, theme, copied }: { email: string | null; view?: string; theme: ProcessedTheme; copied: boolean }): string {
  const subject = `Backoffice bug report${view ? ` — ${view}` : ''}`
  const bodyLines = [
    copied
      ? 'A screenshot of this page was copied to your clipboard — paste it here (Ctrl/Cmd+V) before sending.'
      : 'No screenshot was attached this time — feel free to add one manually.',
    '',
    'What happened:',
    '',
    '',
    'Steps to reproduce:',
    '',
    '',
    '--- Context (auto-filled) ---',
    `URL: ${window.location.href}`,
    `View: ${view ?? 'n/a'}`,
    `Theme: ${theme}`,
    `Viewport: ${window.innerWidth}x${window.innerHeight}`,
    `Browser: ${navigator.userAgent}`,
    `Time: ${new Date().toISOString()}`,
  ]
  return `mailto:${encodeURIComponent(email ?? FALLBACK_BUGS_EMAIL)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(bodyLines.join('\n'))}`
}

type Phase = 'idle' | 'confirm' | 'capturing' | 'preview' | 'error'

// Shared state machine behind every rendering of the widget (floating
// button, sidebar footer row, collapsed-sidebar icon) — only the trigger
// button's chrome differs between them; the dialog content is identical.
function useBugReport({ theme, view }: { theme: ProcessedTheme; view?: string }) {
  const [phase, setPhase] = useState<Phase>('idle')
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [copyError, setCopyError] = useState<string | null>(null)
  const [previewBlob, setPreviewBlob] = useState<Blob | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [email, setEmail] = useState<string | null>(null)
  const copying = useRef(false)

  useEffect(() => {
    fetch('/api/bugreport/config')
      .then((r) => r.json())
      .then((payload) => {
        if (payload?.success && payload?.data?.email) setEmail(payload.data.email)
      })
      .catch(() => { /* falls back to FALLBACK_BUGS_EMAIL */ })
  }, [])

  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl) }, [previewUrl])

  const reset = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl)
    setPhase('idle')
    setErrorMsg(null)
    setCopyError(null)
    setPreviewBlob(null)
    setPreviewUrl(null)
  }

  const openEmail = (copied: boolean) => {
    window.location.href = buildMailto({ email, view, theme, copied })
    reset()
  }

  const openWidget = () => setPhase('confirm')

  const startCapture = async () => {
    setPhase('capturing')
    try {
      const stream = await requestScreenStream()
      if (!stream) {
        // User dismissed/cancelled the share picker — not an error, just
        // proceed straight to the email without a screenshot.
        openEmail(false)
        return
      }
      const blob = await captureScreenshotBlob(stream)
      setPreviewBlob(blob)
      setPreviewUrl(URL.createObjectURL(blob))
      setPhase('preview')
    } catch (err: any) {
      const message = err?.name ? `${err.name}: ${err?.message ?? 'unknown error'}` : (err?.message ?? 'Screenshot capture failed')
      console.error('[BugReportWidget] screenshot capture failed', err)
      setErrorMsg(message)
      setPhase('error')
    }
  }

  // This click is the fresh user gesture the clipboard write needs —
  // deliberately kept separate from the click that started getDisplayMedia.
  const copyAndOpenEmail = async () => {
    if (!previewBlob || copying.current) return
    copying.current = true
    try {
      await writeBlobToClipboard(previewBlob)
      openEmail(true)
    } catch (err: any) {
      console.error('[BugReportWidget] clipboard write failed', err)
      setCopyError(err?.name ? `${err.name}: ${err?.message ?? 'unknown error'}` : (err?.message ?? 'Copy failed'))
    } finally {
      copying.current = false
    }
  }

  return { phase, errorMsg, copyError, previewUrl, openWidget, startCapture, copyAndOpenEmail, openEmail, reset }
}

type BugReportVariant = 'floating' | 'sidebar' | 'sidebar-collapsed'

export function BugReportWidget({
  theme,
  view,
  variant = 'floating',
}: {
  theme: ProcessedTheme
  view?: string
  variant?: BugReportVariant
}) {
  const isDark = theme === 'dark'
  const { phase, errorMsg, copyError, previewUrl, openWidget, startCapture, copyAndOpenEmail, openEmail, reset } = useBugReport({ theme, view })
  const capturing = phase === 'capturing'

  const dialog = (
    <AlertDialog open={phase !== 'idle'} onOpenChange={(open) => { if (!open) reset() }}>
      <AlertDialogContent>
        {phase === 'confirm' && (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle>Report a bug</AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-2 text-left">
                  <p>Clicking Continue asks your browser for permission to share this tab — pick <strong>“This Tab”</strong> (or similar) in the prompt that appears next.</p>
                  <p>You’ll get a preview of the screenshot and a button to copy it before your email client opens with the report pre-filled.</p>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={(e) => { e.preventDefault(); startCapture() }}>Continue</AlertDialogAction>
            </AlertDialogFooter>
          </>
        )}

        {phase === 'capturing' && (
          <div className="flex flex-col items-center gap-3 py-6">
            <Loader2 className="h-6 w-6 animate-spin text-signal" />
            <div className="text-sm text-muted-foreground">Waiting for the share picker…</div>
          </div>
        )}

        {phase === 'preview' && (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle>Screenshot captured</AlertDialogTitle>
              <AlertDialogDescription>
                Copy it to your clipboard, then paste it (Ctrl/Cmd+V) into the email that opens.
              </AlertDialogDescription>
            </AlertDialogHeader>
            {previewUrl && (
              <img src={previewUrl} alt="Captured screenshot preview" className="max-h-[40vh] w-full rounded-md border object-contain" />
            )}
            {copyError && (
              <div className="rounded-md border border-destructive/40 bg-danger-surface px-3 py-2 text-[11px] text-destructive dark:border-destructive/40 dark:bg-danger-surface dark:text-destructive">
                <span className="select-text font-mono">{copyError}</span> — you can still open the email and attach the screenshot manually.
              </div>
            )}
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => openEmail(false)}>Skip, just open email</AlertDialogCancel>
              <AlertDialogAction onClick={(e) => { e.preventDefault(); copyAndOpenEmail() }}>Copy &amp; open email</AlertDialogAction>
            </AlertDialogFooter>
          </>
        )}

        {phase === 'error' && (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle>Screenshot capture failed</AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-2 text-left">
                  <p className="select-text rounded-md border border-destructive/40 bg-danger-surface px-3 py-2 font-mono text-[11px] text-destructive dark:border-destructive/40 dark:bg-danger-surface dark:text-destructive">
                    {errorMsg}
                  </p>
                  <p>You can still open the email and attach a screenshot manually.</p>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Close</AlertDialogCancel>
              <AlertDialogAction onClick={(e) => { e.preventDefault(); openEmail(false) }}>Open email anyway</AlertDialogAction>
            </AlertDialogFooter>
          </>
        )}
      </AlertDialogContent>
    </AlertDialog>
  )

  if (variant === 'sidebar-collapsed') {
    return (
      <>
        {dialog}
        <button
          onClick={openWidget}
          disabled={capturing}
          title="Report a bug"
          aria-label="Report a bug"
          className={`flex h-7 w-7 items-center justify-center rounded transition-colors disabled:opacity-60 text-muted-foreground hover:bg-secondary hover:text-muted-foreground`}
        >
          {capturing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Bug className="h-3.5 w-3.5" />}
        </button>
      </>
    )
  }

  if (variant === 'sidebar') {
    return (
      <>
        {dialog}
        <button
          onClick={openWidget}
          disabled={capturing}
          title="Report a bug"
          className={`flex h-9 w-full items-center gap-3 rounded-sm px-2 text-sm font-medium transition-colors disabled:opacity-60 text-muted-foreground hover:bg-card`}
        >
          {capturing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bug className="h-4 w-4" />}
          Report a bug
        </button>
      </>
    )
  }

  // 'floating' — used on screens that don't have the sidebar chrome at all
  // (sign-in, access-denied, initial loading), so the widget still has
  // somewhere to live.
  return (
    <div className="fixed bottom-4 right-4 z-[90]">
      {dialog}
      <button
        onClick={openWidget}
        disabled={capturing}
        title="Report a bug"
        aria-label="Report a bug"
        className={`flex h-11 w-11 items-center justify-center rounded-full border shadow-notice transition-colors disabled:opacity-60 border-border bg-card text-muted-foreground hover:border-signal hover:text-signal`}
      >
        {capturing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bug className="h-4 w-4" />}
      </button>
    </div>
  )
}
